import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { TileLayer } from "react-leaflet";

import MapConfig from "services/map/MapConfig";
import AreasLayers from "components/AreasLayers";
import PointsLayers from "components/PointsLayers";
import PathsLayers from "components/PathsLayers";

import LeafletMap from "components/LeafletMap";
import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import FlowActioner from "services/flow/actioner";
import FlowUtils from "services/flow/utils";

function MapComponent({
  center,
  flowDetails,
  flowFilters,
  flowMap,
  initFloorsData,
  maxZoom,
  minZoom,
  onRecenter,
  retrieveLayersInfos,
  setConfigParam,
  tilesUrl,
  zoom,
  setSelectedLayers,
}) {
  const [leafletMap, setLeafletMap] = useState();

  useEffect(() => {
    async function initializeData() {
      if (flowMap?.initialized) {
        initFloorsData();
        return null;
      }

      await initFloorsData();
      await retrieveLayersInfos();
    }

    initializeData();
  }, [flowMap?.initialized, initFloorsData, retrieveLayersInfos]);

  useEffect(() => {
    if (flowMap?.initialized && leafletMap) {
      onRecenter(leafletMap?.leafletElement);
    }
  }, [flowMap?.initialized, leafletMap]);

  function getMapRadius(currentMap) {
    return MapUtils.getRadiusInMeters(
      currentMap.getBounds().getNorthEast(),
      currentMap.getCenter()
    );
  }

  function handleClosePopup() {
    return leafletMap?.leafletElement?.closePopup();
  }

  function handleZoomEnd(e) {
    const currentMap = e?.target;

    handleClosePopup();
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentZoom", currentMap.getZoom());
  }

  function handleMoveEnd(e) {
    const currentMap = e?.target;

    handleClosePopup();
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentPosition", currentMap.getCenter());
  }

  function handleOnLoad(e) {
    const currentMap = e?.target;
    setConfigParam("currentRadius", getMapRadius(currentMap._map));
    setConfigParam("currentPosition", currentMap._map.getCenter());
  }

  function handleSelectLayers(layers, clickedLayer) {
    // one layer selected at a time
    setSelectedLayers([clickedLayer]);
  }

  function handleIsPointSelected(point) {
    const selectedLayers = flowMap?.selectedLayers || [];

    return !!selectedLayers.find((layer) => {
      return (
        (layer?.id &&
          (layer?.id === point?.area_id || layer?.id === point?.id)) ||
        !!(layer?.ref_flow_points || []).find((itemId) => itemId === point?.id)
      );
    });
  }

  function handleHasLabelBackground(layer) {
    const type = layer?.type;
    const layerOptions = layer?.options;

    if (
      type === "point" &&
      !layerOptions?.background?.out &&
      !layerOptions?.background?.in
    ) {
      return false;
    }

    if (
      type === "area" &&
      !layerOptions?.background?.out &&
      !layerOptions?.background?.in
    ) {
      return false;
    }

    return layer?.selected;
  }

  function handleGetLayerLabel(layer) {
    if (layer.type === "point" || layer.type === "area") {
      return layer?.short_name || layer?.long_name || null;
    }

    return null;
  }

  function handleGetAreaLabelIcon(layer) {
    if (layer?.options?.arrow_icon) {
      return FlowUtils.getPointIcon(layer?.options?.arrow_icon);
    }

    return MapConfig?.iconByType?.flow?.[layer.type]?.[layer.apiType];
  }

  return (
    <LeafletMap
      preferCanvas={false}
      style={{
        width: "100%",
        height: "100%",
      }}
      setMapRef={setLeafletMap}
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      onZoomEnd={handleZoomEnd}
      onMoveEnd={handleMoveEnd}
    >
      <TileLayer url={MapConfig.defaultTileUrl} />
      <TileLayer
        url={tilesUrl}
        onLoad={handleOnLoad}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />

      <AreasLayers
        map={flowMap}
        options={flowDetails}
        onSelectLayers={handleSelectLayers}
        getLabel={handleGetLayerLabel}
        getLabelIcon={handleGetAreaLabelIcon}
        hasLabelBackground={handleHasLabelBackground}
        filters={flowFilters}
      />

      <PathsLayers
        map={flowMap}
        options={flowDetails}
        onSelectLayers={handleSelectLayers}
        getLabelIcon={handleGetAreaLabelIcon}
        getLabel={(path) => {
          return path?.short_name;
        }}
        hasLabelBackground={handleHasLabelBackground}
        filters={flowFilters}
        getBackground={(path) => {
          return {
            config: {
              mainColor: path?.options?.selected_color,
            },
          };
        }}
      />

      <PointsLayers
        map={flowMap}
        options={flowDetails}
        onSelectLayers={handleSelectLayers}
        isPointSelected={handleIsPointSelected}
        filters={flowFilters}
        getLabelIcon={handleGetAreaLabelIcon}
        getLabel={handleGetLayerLabel}
        hasLabelBackground={handleHasLabelBackground}
      />
    </LeafletMap>
  );
}

Map.propTypes = {
  center: PropTypes.array.isRequired,
  zoom: PropTypes.number.isRequired,
  minZoom: PropTypes.number.isRequired,
  maxZoom: PropTypes.number.isRequired,
  initFloorsData: PropTypes.func,
  analysisMarkers: PropTypes.object,
  tilesUrl: PropTypes.string,
  zones: PropTypes.object,
};

const mapStateToProps = (state) => ({
  center: MapUtils.getCenter(state.Map),
  flowDetails: state.Flow.details,
  flowFilters: state.Flow?.filters,
  flowMap: state.Flow.map,
  maxZoom: MapUtils.getMaxZoom(state.Map),
  minZoom: MapUtils.getMinZoom(state.Map),
  tilesUrl: MapUtils.getTilesUrl(state.Map),
  zoom: MapUtils.getZoom(state.Map),
});

const mapDispatchToProps = (dispatch) => ({
  initFloorsData: () => dispatch(MapActioner.initFloorsDataFromModule("flow")),
  retrieveLayersInfos: () => dispatch(FlowActioner.retrieveLayersInfos()),
  setConfigParam: (key, value) =>
    dispatch(MapActioner.setConfigParam(key, value)),
  setSelectedLayers: (layers) =>
    dispatch(FlowActioner.setSelectedLayers(layers)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
